import {
    argentinaLigaCopa,
    forcedCupCompetitionIds,
    forcedRegularSeasonCompetitionIds,
    optaCompetitionId,
    optaStage,
} from "./constants";

// Reglas puras de competición: deciden a partir de la data de Opta
// Mismos nombres y firmas que competitionRules.js de Olé, para que los dos repos se lean igual

/** Slug del final de la url del menú: `/deportes/estadisticas/futbol/primera-a.html` -> `/primera-a`. */
const slugFromCompetitionUrl = (url) => {
    const match = String(url ?? "").match(/\/([^/]+)\.html$/);
    return match?.[0] ? match[0].slice(0, -5) : null;
};

/**
 * Busca en el menú del CMS la competición cuya URL vieja coincide con el slug de la ruta.
 * El menú es la única fuente del id de Opta.
 */
export const findMenuCompetition = (competitionSlug, menuCompetitions = []) => {
    const menuCompetition = menuCompetitions.find(({ url }) => slugFromCompetitionUrl(url) === competitionSlug);
    if (!menuCompetition) return null;

    return {
        ...menuCompetition,
        isArgentinaLigaCopa: !menuCompetition.liga && competitionSlug === argentinaLigaCopa.slug,
    };
};

/** Cabecera de la competición: sólo los campos que pinta la vista. */
export const toCompetitionHeader = (competition) => {
    const { name, id, country, countryId, competitionFormat, tournamentCalendar } = competition;
    return { name, id, country, countryId, competitionFormat, tournamentCalendar };
};

/**
 * Si la competición se muestra como copa (llaves y fixture completo) o como liga
 * (con tabla de posiciones y jornadas).
 * la declara el feed, la fuerza un id conocido o la marca el CMS.
 */
export const isCupCompetition = (competition, menuCompetition) =>
    Boolean(
        competition?.competitionFormat?.includes("cup") ||
        forcedCupCompetitionIds.includes(competition?.id) ||
        menuCompetition?.isCup
    );

/** Fase regular: la declara la temporada vigente o la fuerza un id conocido. */
export const hasRegularSeason = (competition) =>
    Boolean(
        competition?.tournamentCalendar?.[0]?.stage?.some(({ name }) => name === optaStage.regularSeason) ||
        forcedRegularSeasonCompetitionIds.includes(competition?.id)
    );

/** Temporada a mostrar: la que coincide con el año pedido, con la última publicada como respaldo. */
export const pickCalendarId = (competition, calendarName) => {
    const calendars = competition?.tournamentCalendar ?? [];
    return calendars.find(({ name }) => name?.includes(String(calendarName)))?.id ?? calendars[0]?.id ?? null;
};

/** Temporada puntual por nombre exacto (la Copa de la Liga vive en una sola). */
export const findCalendarIdByName = (competition, calendarName) =>
    competition?.tournamentCalendar?.find(({ name }) => name === calendarName)?.id ?? null;

/**
 * En etapas de todos contra todos la jornada del próximo partido es la jornada en curso.
 * En etapas de eliminación no lo es, y hay que caer a la última publicada.
 */
export const isLeagueStageMatch = (match) => {
    const stageName = match?.matchInfo?.stage?.name;
    if (!stageName) return false;

    return (
        stageName === optaStage.firstPhase ||
        stageName === optaStage.regularSeason ||
        stageName.includes(optaStage.group) ||
        stageName.includes(optaStage.apertura) ||
        stageName.includes(optaStage.clausura) ||
        (match.matchInfo?.competition?.id === optaCompetitionId.ligaProfesional &&
            stageName === optaStage.secondPhase)
    );
};

/** La Copa de la Liga sólo cuenta jornadas de su 2da fase. */
export const isSecondPhaseMatch = (match) => match?.matchInfo?.stage?.name === optaStage.secondPhase;

/** Copia con `matchInfo` extendido */
const withMatchInfo = (match, extra) => ({ ...match, matchInfo: { ...match.matchInfo, ...extra } });

/**
 * El torneo argentino (primera-division) y la Copa de la Liga comparten el mismo `tournamentCalendar` en Opta y
 * se distinguen sólo por la etapa: la 2da fase es la Copa de la Liga y lleva su nombre
 * editorial, el resto es Liga Profesional. Cada vista marca así los partidos de ese calendario.
 */
export const labelLigaCopaMatches = (matches = []) =>
    matches.map((match) =>
        isSecondPhaseMatch(match)
            ? withMatchInfo(match, {
                  competition: { ...match.matchInfo.competition, name: argentinaLigaCopa.displayName },
              })
            : withMatchInfo(match, { isLigaProfesional: true })
    );

/** Tabla anual argentina: suma la fase total con la 2da fase. */
export const getPositionsAnualArgentina = (positions, competitionStage = 1) => {
    const totalPositions = positions?.stage[0].division.filter(item => item.type === "total")?.flatMap(item => item.ranking) || [];
        const actualStage = getStageName(positions?.competition?.id, competitionStage);
    const secondPhasePositions = positions?.stage.find(item => item.name === actualStage)?.division.filter(item => item.type === "total").flatMap(item => item.ranking) || [];

    totalPositions.map(team => {
        const teamId = team.contestantId;
        const teamPos2 = secondPhasePositions.find(item => item.contestantId === teamId);

        if (teamId && teamPos2) {
            const fieldsToSum = ["points", "matchesPlayed", "matchesWon", "matchesLost", "matchesDrawn", "goalsFor", "goalsAgainst", "goaldifference"];
            fieldsToSum.map(field => {
                team[field] = parseInt(team[field]) + parseInt(teamPos2[field]);
            });
        }
        return team;
    });

    return [{ ranking: totalPositions }];
};

/** Nombre de etapa por competición, para filtrar el fixture. */
/**
 * Nombres de las dos etapas de cada torneo que se divide en dos.
 */
const stageNamesByCompetition = {
    [optaCompetitionId.ligaMxMexico]: [optaStage.apertura, optaStage.clausura],
    [optaCompetitionId.ligaProfesional]: [optaStage.firstPhase, optaStage.secondPhase, optaStage.apertura, optaStage.clausura],
};

export const getStageName = (competitionId, competitionStage) => {
    const stages =
        stageNamesByCompetition[competitionId] ??
        stageNamesByCompetition[optaCompetitionId.ligaProfesional];
    return stages[competitionStage];
};

export const getMatchesByStage = (fixture, competitionStage) => {
    const competitionId = fixture?.[0]?.matchInfo?.competition?.id;
    const stageName = getStageName(competitionId, competitionStage);
    return fixture.filter(item => item.matchInfo?.stage?.name.includes(stageName))
}

/** Temporada vigente de una lista de calendarios. */
export const getCalendarId = (calendars) => {
    const currentYear = new Date().getFullYear();
    return calendars.find(item => new Date(item.startDate).getFullYear() <= currentYear)?.id;
};
